import { Suspense } from 'react'
import { Outlet, Navigate, useLocation } from 'react-router-dom'
import KostenstellenSubNav from './components/KostenstellenSubNav'
import RoleGuard from '../../../shell/RoleGuard'
import { modules } from '../../registry'

// ── Skeleton loader ───────────────────────────────────────────────────────────

function SurfaceSkeleton() {
  return (
    <div className="flex-1 p-6 space-y-4">
      <div className="h-8 w-56 rounded-xl bg-surface-container-low animate-pulse" />
      <div className="grid grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-24 rounded-xl bg-surface-container-low animate-pulse" />
        ))}
      </div>
      <div className="h-48 rounded-xl bg-surface-container-low animate-pulse" />
    </div>
  )
}

// ── Module shell ──────────────────────────────────────────────────────────────
// Renders the inner sub-nav rail + child route surface area.
// Child routes (Imports, Stammdaten, Mapping, etc.) are added by sibling
// Wave 2 agents — Outlet renders nothing until those routes exist, which is
// fine: React Router simply falls through to the parent.

export default function KostenstellenPage() {
  const location = useLocation()

  // Default redirect: bare /verwaltung/kostenstellen → Übersicht
  const isExact = location.pathname === '/verwaltung/kostenstellen' ||
                  location.pathname === '/verwaltung/kostenstellen/'

  // Child surfaces are gated by their registry module (ops.RoleModuleAccess), like the Sidebar:
  // longest registry path that prefixes the current URL (covers /imports/winfood, /auswertung/schule/…).
  // The parent 'verwaltung-kostenstellen' guard in router.tsx stays; this adds the per-surface gate
  // so hiding e.g. Stammdaten in /it/role-admin actually hides the page, not just the sidebar entry.
  // Retired URLs that only <Navigate> elsewhere (router.tsx) must not be gated by the module they happen to sit under.
  const isRedirectShim = ['/verwaltung/kostenstellen/imports/pca', '/verwaltung/kostenstellen/imports/pca-bookings', '/verwaltung/kostenstellen/kiosk'].includes(location.pathname.replace(/\/$/, ''))
  const childModule = isRedirectShim ? undefined : modules
    .filter(m => m.path.startsWith('/verwaltung/kostenstellen/') && (location.pathname === m.path || location.pathname.startsWith(m.path + '/')))
    .sort((a, b) => b.path.length - a.path.length)[0]?.id

  return (
    <div className="flex h-full min-h-0 overflow-hidden">
      {/* Inner left rail — opaque Ledger tone, NOT glass */}
      <KostenstellenSubNav />

      {/* Surface area — TRANSPARENT so the page-level <GlassScene /> orbs
          and gradient (mounted in router.tsx, position:fixed z-0) show
          through, matching the rest of the app's chrome. Cards inside
          (liquid-glass, GlassKpi, PageHeader, data tables with their own
          bg) sit on top and refract against the scene. DESIGN.md §14. */}
      <div className="flex-1 min-w-0 overflow-y-auto bg-transparent">
        <Suspense fallback={<SurfaceSkeleton />}>
          {isExact
            ? <Navigate to="/verwaltung/kostenstellen/uebersicht" replace />
            : childModule
              ? <RoleGuard module={childModule}><Outlet /></RoleGuard>
              : <Outlet />
          }
        </Suspense>
      </div>
    </div>
  )
}
